'use client'

import React, { useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { SubMode } from '@/lib/types'
import { models, ModelId } from '@/lib/models/model-list'
import { ModelSelectorContainer } from '@/components/prompt-area/model-selector'
import { useUserContext } from '../providers'
import { toast } from 'sonner'

export function SubModeEditorDialog({
    open,
    onOpenChange,
    initialValue,
    onSubmit,
    children,
    childIsTrigger = false,
    openOnChildClick = false
}: {
    open?: boolean
    onOpenChange?: (open: boolean) => void
    initialValue?: SubMode | null
    onSubmit: (value: SubMode) => void
    children?: React.ReactNode
    childIsTrigger?: boolean
    openOnChildClick?: boolean
}) {
    const { isUserAuthenticated } = useUserContext()
    const isControlled = typeof open === 'boolean'
    const [internalOpen, setInternalOpen] = useState(false)

    const dialogOpen = isControlled ? (open as boolean) : internalOpen
    const setDialogOpen = (val: boolean) => {
        if (!isControlled) setInternalOpen(val)
        if (onOpenChange) onOpenChange(val)
    }

    const [label, setLabel] = useState(initialValue?.label || '')
    const [description, setDescription] = useState(initialValue?.description || '')
    const [systemPrompt, setSystemPrompt] = useState(initialValue?.systemPrompt || '')
    const [modelId, setModelId] = useState<ModelId>(initialValue?.modelId || 'openrouter:openai/gpt-5-nano')

    const selectedModel = useMemo(() => models[modelId], [modelId])

    const handleSave = () => {
        if (!isUserAuthenticated) {
            toast.error("Sign in to create custom mode", {
                action: {
                    label: "Sign in",
                    onClick: () => window.location.href = "/auth/sign-in"
                }
            })
            return
        }
        const newValue: SubMode = {
            label: label.trim(),
            modelId,
            systemPrompt,
            description: description.trim() || undefined,
            type: 'text'
        }
        onSubmit(newValue)
        setDialogOpen(false)
    }

    const isValid = label.trim().length > 0 && !!selectedModel && systemPrompt.trim().length > 0

    return (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            {children && (
                openOnChildClick ? (
                    <span className="contents" onClick={() => setDialogOpen(true)}>{children}</span>
                ) : childIsTrigger ? (
                    <>{children}</>
                ) : (
                    <DialogTrigger asChild className="cursor-pointer">
                        {children}
                    </DialogTrigger>
                )
            )}
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>{initialValue ? 'Edit sub-mode' : 'Create sub-mode'}</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-3">
                    <div>
                        <label className="text-sm text-muted-foreground">Label</label>
                        <Input
                            value={label}
                            onChange={e => setLabel(e.target.value)}
                            placeholder="e.g., Conversational, Search, Quick"
                        />
                    </div>

                    <div>
                        <label className="text-sm text-muted-foreground">Model</label>
                        <div className="mt-1">
                            <ModelSelectorContainer
                                model={selectedModel}
                                onModelChange={m => setModelId(m.id as ModelId)}
                                modelType={'text'}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm text-muted-foreground">System prompt</label>
                        <Textarea
                            value={systemPrompt}
                            onChange={e => setSystemPrompt(e.target.value)}
                            placeholder="Behavioral instructions for this sub-mode (required)"
                            rows={6}
                        />
                    </div>

                    <div>
                        <label className="text-sm text-muted-foreground">Description (optional)</label>
                        <Input
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="Short description shown in UI"
                        />
                    </div>

                    <div className="flex justify-end gap-2 mt-2">
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button disabled={!isValid} onClick={handleSave}>Save</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}


